import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  getQuotes,
  getOrderStats,
  getOrderForEdit,
  createOrder,
  updateOrder,
  getCustomerOrganisations,
  getSalesUsers,
  getOrganisationUsers,
  getContractsByCustomer,
  getSitesByCustomer,
} from '../api';
import { orderKeys } from './queryKeys';
import { ORDER_STATUSES } from '../types';
import type { QuotesFilters, CreateQuoteInput, UpdateQuoteInput } from '../types';

// Helper to extract error message
function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return 'An unexpected error occurred';
}

// =============================================================================
// Query Hooks
// =============================================================================

/**
 * Hook to fetch orders (quotes filtered to order statuses)
 */
export function useOrders(filters: QuotesFilters = {}) {
  // Default to order statuses if no status filter provided
  const orderFilters: QuotesFilters = {
    ...filters,
    status_in: filters.status_in && filters.status_in.length > 0
      ? filters.status_in
      : ORDER_STATUSES,
  };

  return useQuery({
    queryKey: orderKeys.list(orderFilters),
    queryFn: () => getQuotes(orderFilters),
  });
}

/**
 * Hook to fetch order stats via server-side V4 endpoint
 */
export function useOrderStats(filters: {
  accountId?: string;
  salesManagerId?: string;
  status_in?: string[];
} = {}) {
  const statsFilters = {
    ...filters,
    status_in: filters.status_in && filters.status_in.length > 0
      ? filters.status_in
      : ORDER_STATUSES as string[],
  };

  return useQuery({
    queryKey: orderKeys.stats(statsFilters),
    queryFn: () => getOrderStats(statsFilters),
    staleTime: 60 * 1000,
  });
}

/**
 * Hook to fetch a single order for editing (V3 full data)
 */
export function useOrderForEdit(id: string | null | undefined) {
  return useQuery({
    queryKey: orderKeys.edit(id!),
    queryFn: () => getOrderForEdit(id!),
    enabled: !!id,
  });
}

/**
 * Hook to create a new order
 */
export function useCreateOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateQuoteInput) => createOrder(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: orderKeys.stats() });
      toast.success('Order created successfully');
    },
    onError: (error) => {
      toast.error('Failed to create order', { description: getErrorMessage(error) });
    },
  });
}

/**
 * Hook to update an order
 */
export function useUpdateOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateQuoteInput }) => updateOrder(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: orderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: orderKeys.stats() });
      queryClient.invalidateQueries({ queryKey: orderKeys.details() });
      queryClient.invalidateQueries({ queryKey: orderKeys.edit(variables.id) });
      toast.success('Order updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update order', { description: getErrorMessage(error) });
    },
  });
}

/**
 * Hook to fetch customer organisations for filter dropdown
 */
export function useCustomerOrganisations() {
  return useQuery({
    queryKey: orderKeys.customers(),
    queryFn: getCustomerOrganisations,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch sales users for dropdowns
 */
export function useSalesUsers() {
  return useQuery({
    queryKey: orderKeys.salesUsers(),
    queryFn: getSalesUsers,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch users for a specific organisation
 */
export function useUsers(orgId: string | undefined) {
  return useQuery({
    queryKey: orderKeys.users(orgId!),
    queryFn: () => getOrganisationUsers(orgId!),
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch subscriptions (V4 contracts) for a customer
 */
export function useSubscriptions(customerId: string | undefined) {
  return useQuery({
    queryKey: orderKeys.subscriptions(customerId!),
    queryFn: () => getContractsByCustomer(customerId!),
    enabled: !!customerId,
    staleTime: 5 * 60 * 1000,
  });
}

/** @deprecated Use useSubscriptions instead */
export function useContracts(customerId: string | undefined) {
  return useSubscriptions(customerId);
}

/**
 * Hook to fetch sites for a customer
 */
export function useSites(customerId: string | undefined) {
  return useQuery({
    queryKey: [...orderKeys.all, 'sites', customerId] as const,
    queryFn: () => getSitesByCustomer(customerId!),
    enabled: !!customerId,
    staleTime: 5 * 60 * 1000,
  });
}
